/**
 * Time as an injected dependency.
 *
 * Every deadline in this system (SAD §4 lists seven) is computed from a `Clock`, never from
 * `Date.now()` directly. This is what makes the timer logic testable without a single `sleep()`
 * in the suite — see docs/plan/06-risks.md R5.
 *
 * A lint rule forbids `Date.now()` and `new Date()` outside this module's system implementation.
 */
/**
 * The production clock, reading the host's wall time.
 *
 * The two reads below are the only permitted wall-clock reads in the system. `eslint.config.js`
 * grants the exemption to this file by path rather than by inline disable comment, so the
 * exemption is visible where the rule is defined.
 */
export class SystemClock {
    now() {
        return new Date();
    }
    nowMs() {
        return Date.now();
    }
}
/**
 * A clock under test control.
 *
 * Advance it explicitly to drive deadline expiry deterministically:
 *
 * ```ts
 * const clock = new FakeClock(new Date('2026-07-23T10:00:00Z'));
 * clock.advanceBy(minutes(10));   // reservation TTL now due
 * ```
 */
export class FakeClock {
    current;
    constructor(start = new Date('2026-07-23T00:00:00.000Z')) {
        this.current = start.getTime();
    }
    now() {
        return new Date(this.current);
    }
    nowMs() {
        return this.current;
    }
    /** Move time forward by a duration in milliseconds. */
    advanceBy(ms) {
        if (ms < 0)
            throw new RangeError('FakeClock cannot move backwards');
        this.current += ms;
    }
    /** Jump to a specific instant. */
    setTo(instant) {
        this.current = instant.getTime();
    }
}
/** Convert seconds to milliseconds. */
export const seconds = (n) => n * 1_000;
/** Convert minutes to milliseconds. */
export const minutes = (n) => n * 60_000;
/** Convert hours to milliseconds. */
export const hours = (n) => n * 3_600_000;
/** Convert days to milliseconds. */
export const days = (n) => n * 86_400_000;
/** Add a duration in milliseconds to an instant. */
export function addMs(instant, ms) {
    return new Date(instant.getTime() + ms);
}
/**
 * The Unix epoch, as a fresh `Date` each call.
 *
 * A sentinel for "this row has no meaningful instant" — never a time anything is compared
 * against for a deadline. It lives here because the R5 lint rule bans the `Date` global outside
 * this module, and a function (rather than an exported constant) so callers cannot mutate a
 * shared instance out from under each other.
 */
export function epoch() {
    return new Date(0);
}
/**
 * Parse an ISO-8601 instant supplied by a caller or read back out of a store.
 *
 * Not a clock read: the instant comes from the input, not from the host. It lives here for the
 * same reason {@link epoch} does — the R5 rule bans the `Date` global everywhere else, and
 * routing the legitimate non-clock constructions through this module is what lets the ban stay
 * absolute instead of accumulating inline exemptions at each call site.
 *
 * Invalid input yields an `Invalid Date`, exactly as `new Date(value)` does; callers that care
 * validate with a schema before getting here.
 */
export function fromIso(value) {
    return new Date(value);
}
/**
 * Build a `Date` from epoch milliseconds.
 *
 * The sibling of {@link fromIso}, and here for the same reason: `new Date(n)` is arithmetic on a
 * value somebody already supplied, not a wall-clock read, but the lint rule that keeps
 * `Date.now()` out of the codebase cannot tell the two apart. Granting the exemption to this file
 * by path keeps it absolute instead of accumulating inline disables wherever a window is computed.
 */
export function fromEpochMs(ms) {
    return new Date(ms);
}
//# sourceMappingURL=clock.js.map